/**
 * @fileoverview 船員アプリ: 自分の資格・期限（プロフィール画面の1セクション）
 * @description 陸上が登録した海技免状・船員手帳・STCW・健康証明書などの有効期限を本人が確認できる。
 *              閲覧のみ（更新は陸上が登録）。種別マスタで「本人に見せない」にした資格は出ない。
 *              船長（役職者）には自船の乗組員分も出す（種別マスタで船長に見せる設定のもの）。
 *              機能フラグ crew_certificates の会社のみ。圏外では直前に取得した内容をそのまま出す。
 */
import React, { useEffect, useState } from 'react';
import { apiFetch } from '../services/api';

type Level = 'none' | 'ok' | 'warn' | 'danger' | 'expired' | 'renewing';
interface Cert { id: string; type_name: string; type_color: string; grade: string | null; cert_no: string | null; issued_date: string | null; expiry_date: string | null; status: string; level: Level; days: number | null }
interface Missing { type_id: string; type_name: string; type_color: string }
interface VesselCrew { user_id: string; user_name: string; position: string | null; certs: { id: string; type_name: string; type_color: string; grade: string | null; expiry_date: string | null; level: Level; days: number | null }[] }

const STYLE: Record<Level, { bg: string; fg: string; label: string }> = {
  expired:  { bg: '#fee2e2', fg: '#b91c1c', label: '期限切れ' },
  danger:   { bg: '#fee2e2', fg: '#b91c1c', label: '期限が近い' },
  warn:     { bg: '#fef3c7', fg: '#b45309', label: 'もうすぐ期限' },
  renewing: { bg: '#dbeafe', fg: '#1d4ed8', label: '更新手続き中' },
  ok:       { bg: '#f0fdf4', fg: '#16a34a', label: '有効' },
  none:     { bg: '#f1f5f9', fg: '#64748b', label: '期限なし' },
};
const fmt = (v: string | null) => (v ? v.slice(0, 10).replace(/-/g, '/') : '-');
const daysText = (d: number | null) => d == null ? '' : d < 0 ? `${-d}日過ぎています` : d === 0 ? '本日まで' : `あと${d}日`;

const CACHE_KEY = 'catms_my_certificates';

export const MyCertificates: React.FC<{ isOfficer: boolean }> = ({ isOfficer }) => {
  const [certs, setCerts] = useState<Cert[] | null>(() => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null'); } catch { return null; } });
  const [missing, setMissing] = useState<Missing[]>([]);
  const [vessel, setVessel] = useState<{ name: string; crew: VesselCrew[] } | null>(null);
  const [showVessel, setShowVessel] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    apiFetch<any>('/crew-certificates/me')
      .then(r => { const list = (r.data ?? []) as Cert[]; setCerts(list); setMissing((r.missing ?? []) as Missing[]); try { localStorage.setItem(CACHE_KEY, JSON.stringify(list)); } catch { /* noop */ } })
      .catch(() => setOffline(true));
    if (isOfficer) {
      apiFetch<any>('/crew-certificates/me/vessel')
        .then(r => { if (r.vessel) setVessel({ name: r.vessel.vessel_name, crew: (r.data ?? []) as VesselCrew[] }); })
        .catch(() => { /* 権限なし・圏外は出さない */ });
    }
  }, [isOfficer]);

  if (certs == null) return null;
  const visible = certs.filter(c => c.status === 'active' || c.status === 'renewing');
  const urgent = visible.filter(c => c.level === 'expired' || c.level === 'danger');

  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ padding: '0 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '0 4px', marginBottom: 6, marginTop: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
        {title}{offline && <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '0 5px' }}>圏外・前回の内容</span>}
      </div>
      <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>{children}</div>
    </div>
  );

  return (
    <>
      <Section title="資格・証明書の有効期限">
        {urgent.length > 0 && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, fontWeight: 700, padding: '10px 16px', borderBottom: '1px solid #fecaca' }}>
            ⚠️ 期限切れ・期限が近いものが {urgent.length} 件あります。陸上の担当者へ更新の手続きを確認してください。
          </div>
        )}
        {visible.length === 0 && missing.length === 0 && <div style={{ padding: '16px', fontSize: 13, color: '#94a3b8' }}>登録されている資格はありません。</div>}
        {visible.map((c, i) => {
          const s = STYLE[c.level];
          return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
              <span style={{ width: 6, alignSelf: 'stretch', borderRadius: 3, background: c.type_color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{c.type_name}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.grade || (c.cert_no ? `番号 ${c.cert_no}` : '登録あり')}</div>
                {c.grade && c.cert_no && <div style={{ fontSize: 11.5, color: '#64748b' }}>番号 {c.cert_no}</div>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{c.expiry_date ? fmt(c.expiry_date) : '期限なし'}</div>
                <span style={{ display: 'inline-block', marginTop: 3, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.fg }}>
                  {c.level === 'renewing' ? s.label : c.days == null ? s.label : daysText(c.days)}
                </span>
              </div>
            </div>
          );
        })}
        {missing.map(m => (
          <div key={m.type_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: '1px solid #f1f5f9', opacity: 0.8 }}>
            <span style={{ width: 6, alignSelf: 'stretch', borderRadius: 3, background: m.type_color, flexShrink: 0 }} />
            <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: '#94a3b8' }}>{m.type_name}</div><div style={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>未登録</div></div>
            <span style={{ fontSize: 11, color: '#64748b' }}>陸上で登録が必要です</span>
          </div>
        ))}
        <div style={{ padding: '8px 16px', fontSize: 11, color: '#94a3b8', borderTop: '1px solid #f1f5f9' }}>期限の登録・更新は陸上の担当者が行います。内容が違うときは連絡してください。</div>
      </Section>

      {vessel && vessel.crew.length > 0 && (
        <Section title={`${vessel.name} 乗組員の資格・期限（船長向け）`}>
          <button onClick={() => setShowVessel(v => !v)} style={{ width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', background: 'white', fontSize: 14, fontWeight: 600, color: '#0f172a', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
            <span>{vessel.crew.length}名 ／ 要確認 {vessel.crew.reduce((s, u) => s + u.certs.filter(c => c.level === 'expired' || c.level === 'danger').length, 0)}件</span>
            <span style={{ color: '#94a3b8' }}>{showVessel ? '▲' : '▼'}</span>
          </button>
          {showVessel && vessel.crew.map(u => (
            <div key={u.user_id} style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{u.user_name}{u.position && <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 12 }}>　{u.position}</span>}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {u.certs.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>表示できる資格はありません</span>}
                {u.certs.map(c => { const s = STYLE[c.level]; return (
                  <span key={c.id} style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: s.bg, color: s.fg, border: `1px solid ${c.type_color}33` }}>
                    {c.type_name.replace(/（.*?）/g, '')} {c.expiry_date ? fmt(c.expiry_date) : '期限なし'}{c.days != null && c.level !== 'ok' ? `（${daysText(c.days)}）` : ''}
                  </span>
                ); })}
              </div>
            </div>
          ))}
        </Section>
      )}
    </>
  );
};

export default MyCertificates;
